import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Upload, File, X } from 'lucide-react';
import { toast } from 'sonner';
import type { UploadedFile } from './types';

interface FileUploaderProps {
  uploadedFiles: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  onRemoveFile: (fileId: string) => void;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  uploadedFiles,
  onFilesChange,
  onRemoveFile
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return;

    const newFiles: UploadedFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file type
      const allowedTypes = ['.txt', '.pdf', '.json', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!allowedTypes.includes(fileExtension)) {
        toast.error(`File type ${fileExtension} not supported. Please use .txt, .pdf, .json, or .docx files.`);
        continue;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }

      try {
        let content = '';
        
        if (fileExtension === '.txt' || fileExtension === '.json') {
          content = await file.text();
        } else if (fileExtension === '.pdf') {
          // For now, show a message that PDF parsing requires server-side processing
          content = `[PDF file: ${file.name} - Content extraction would require server-side processing]`;
        } else if (fileExtension === '.docx') {
          // For now, show a message that DOCX parsing requires server-side processing
          content = `[DOCX file: ${file.name} - Content extraction would require server-side processing]`;
        }

        const uploadedFile: UploadedFile = {
          id: Date.now().toString() + i,
          name: file.name,
          content,
          type: fileExtension,
          size: file.size
        };

        newFiles.push(uploadedFile);
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error(`Failed to read file ${file.name}`);
      }
    }

    if (newFiles.length > 0) {
      onFilesChange([...uploadedFiles, ...newFiles]);
      toast.success(`${newFiles.length} file(s) uploaded successfully`);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">Context Files</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2"
        >
          <Upload className="h-4 w-4" />
          Upload Files
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".txt,.pdf,.json,.docx"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {uploadedFiles.length > 0 && (
        <div className="grid gap-2">
          {uploadedFiles.map((file) => (
            <Card key={file.id} className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <File className="h-4 w-4 text-muted-foreground" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{file.name}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {file.type.toUpperCase()}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveFile(file.id)}
                className="text-muted-foreground hover:text-destructive"
              >
                <X className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Upload context files (.txt, .pdf, .json, .docx) to provide additional information for content enhancement.
      </p>
    </div>
  );
};